/**
 * What a custom-emoji shortcode is allowed to be, in one place.
 *
 * This character class was written out four times — `Twemoji.tsx`,
 * `RichTextInput.tsx`, `editorDom.ts`, and the server — and the frontend copies
 * carried a comment saying they were copies. The composer's two uses (a prefix
 * as it is typed, a `:token:` in a run of text) now read it from here. It has
 * to agree with the server or the two disagree about what a reaction even is:
 *
 *   - `backend/src/modules/reactions/emoji.ts` — `CUSTOM_SHORTCODE_RE`
 *   - `backend/src/db/schema/groups.ts` — the CHECK constraint on the column
 *
 * NOT UNIFIED WITH `resolveEmojiShortcodes` in `lib/standard-emojis.ts`, and
 * that is deliberate rather than an oversight. That one matches `+` as well,
 * because the curated set has aliases like `:+1:` that a custom emoji may never
 * be named. Two rules that look alike and answer different questions: "could
 * this be a group's emoji?" and "is there a token here at all?".
 */
export const CUSTOM_SHORTCODE_RE = /^[a-z0-9_-]{2,50}$/;

/**
 * Whether a partial word, as it is being typed after a `:`, could still become
 * a shortcode. Case-insensitive because the composer lowercases on insert, not
 * on every keystroke.
 */
export function isShortcodePrefix(text: string): boolean {
  return /^[a-z0-9_-]*$/i.test(text);
}

/** A `:shortcode:` token in a run of text, for the custom-emoji pass. */
export const CUSTOM_SHORTCODE_TOKEN_RE = /:([a-z0-9_-]+):/gi;

/**
 * Whether this reaction value names a group's emoji rather than being a
 * character.
 *
 * A real emoji is never lowercase ascii, so the value tells you which kind it
 * is — which is what lets a renderer stop guessing "no url means unicode".
 * Assuming it did was half of OSO-431: a shortcode handed to a codepoint
 * converter became a request for an image that cannot exist.
 */
export function isCustomShortcode(value: string): boolean {
  return CUSTOM_SHORTCODE_RE.test(value);
}
